/* global axios */
import ApiClient from '../ApiClient';

class ConversationApi extends ApiClient {
  constructor() {
    super('conversations', { accountScoped: true });
  }

  get({
    inboxId,
    status,
    assigneeType,
    page,
    labels,
    teamId,
    conversationType,
    internalView,
    excludeInternalInboxes,
    sortBy,
    updatedWithin,
  }) {
    return axios.get(this.url, {
      params: {
        inbox_id: inboxId,
        team_id: teamId,
        status,
        assignee_type: assigneeType,
        page,
        labels,
        conversation_type: conversationType,
        internal_view: internalView,
        exclude_internal_inboxes: excludeInternalInboxes,
        sort_by: sortBy,
        updated_within: updatedWithin,
      },
    });
  }

  filter(payload) {
    return axios.post(`${this.url}/filter`, payload.queryData, {
      params: {
        page: payload.page,
        exclude_internal_inboxes: payload.excludeInternalInboxes,
        internal_view: payload.internalView,
      },
    });
  }

  search({ q, internalView }) {
    return axios.get(`${this.url}/search`, {
      params: {
        q,
        page: 1,
        internal_view: internalView,
      },
    });
  }

  toggleStatus({ conversationId, status, snoozedUntil = null }) {
    return axios.post(`${this.url}/${conversationId}/toggle_status`, {
      status,
      snoozed_until: snoozedUntil,
    });
  }

  togglePriority({ conversationId, priority }) {
    return axios.post(`${this.url}/${conversationId}/toggle_priority`, {
      priority,
    });
  }

  assignAgent({ conversationId, agentId }) {
    return axios.post(
      `${this.url}/${conversationId}/assignments?assignee_id=${agentId}`,
      {}
    );
  }

  assignTeam({ conversationId, teamId }) {
    const params = { team_id: teamId };
    return axios.post(`${this.url}/${conversationId}/assignments`, params);
  }

  markMessageRead({ id }) {
    return axios.post(`${this.url}/${id}/update_last_seen`);
  }

  markMessagesUnread({ id }) {
    return axios.post(`${this.url}/${id}/unread`);
  }

  toggleTyping({ conversationId, status, isPrivate }) {
    return axios.post(`${this.url}/${conversationId}/toggle_typing_status`, {
      typing_status: status,
      is_private: isPrivate,
    });
  }

  mute(conversationId) {
    return axios.post(`${this.url}/${conversationId}/mute`);
  }

  unmute(conversationId) {
    return axios.post(`${this.url}/${conversationId}/unmute`);
  }

  meta({
    inboxId,
    status,
    assigneeType,
    labels,
    teamId,
    conversationType,
    internalView,
    excludeInternalInboxes,
  }) {
    return axios.get(`${this.url}/meta`, {
      params: {
        inbox_id: inboxId,
        status,
        assignee_type: assigneeType,
        labels,
        team_id: teamId,
        conversation_type: conversationType,
        internal_view: internalView,
        exclude_internal_inboxes: excludeInternalInboxes,
      },
    });
  }

  findOrCreateInternalDirectMessage({ targetUserId }) {
    return axios.post(`${this.url}/direct_message`, {
      target_user_id: targetUserId,
    });
  }

  sendEmailTranscript({ conversationId, email }) {
    return axios.post(`${this.url}/${conversationId}/transcript`, { email });
  }

  updateCustomAttributes({ conversationId, customAttributes }) {
    return axios.post(`${this.url}/${conversationId}/custom_attributes`, {
      custom_attributes: customAttributes,
    });
  }

  answerWhatsAppCall({
    conversationId,
    callId,
    caller,
    callAction = 'answer',
    session = null,
    diagnostics = null,
  }) {
    return axios.post(`${this.url}/${conversationId}/answer_whatsapp_call`, {
      call_id: callId,
      caller,
      call_action: callAction,
      session,
      diagnostics,
    });
  }

  fetchWhatsAppCallPermissionStatus({ conversationId, to = null }) {
    return axios.get(
      `${this.url}/${conversationId}/whatsapp_call_permission_status`,
      {
        params: {
          to,
        },
      }
    );
  }

  sendWhatsAppCallPermissionRequest({ conversationId, message, to = null }) {
    return axios.post(
      `${this.url}/${conversationId}/send_whatsapp_call_permission_request`,
      {
        message,
        to,
      }
    );
  }

  sendWhatsAppCallPermissionRequestTemplate({ conversationId, to = null }) {
    return axios.post(
      `${this.url}/${conversationId}/send_whatsapp_call_permission_request_template`,
      {
        to,
      }
    );
  }

  initiateWhatsAppCall({
    conversationId,
    to = null,
    session,
    bizOpaqueCallbackData = null,
  }) {
    return axios.post(`${this.url}/${conversationId}/initiate_whatsapp_call`, {
      to,
      session,
      biz_opaque_callback_data: bizOpaqueCallbackData,
    });
  }

  fetchParticipants(conversationId) {
    return axios.get(`${this.url}/${conversationId}/participants`);
  }

  updateParticipants({ conversationId, userIds }) {
    return axios.patch(`${this.url}/${conversationId}/participants`, {
      user_ids: userIds,
    });
  }

  getAllAttachments(conversationId) {
    return axios.get(`${this.url}/${conversationId}/attachments`);
  }
}

export default new ConversationApi();
